import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { buildTaskOpenAppMessage } from 'components/MapPage/CorporateViewTabs/NotificationsTab/appMessageTaskLinks';

type TaskApprovalRouteDialogProps = {
  open: boolean;
  title: string;
  actionId: string;
  taskName?: string;
  onOpenChange: (open: boolean) => void;
  /** After notify succeeds; parent appends the request signature. */
  onConfirm: () => void | Promise<void>;
  confirmPending?: boolean;
};

/** Pick roster users and/or ICS positions; notify with a deep link to this task. */
export function TaskApprovalRouteDialog({
  open,
  title,
  actionId,
  taskName = '',
  onOpenChange,
  onConfirm,
  confirmPending = false,
}: TaskApprovalRouteDialogProps) {
  const incidentId = useActualIncidentId();
  const { data: roster = [] } = useIncidentRoster();
  const { data: positions = [] } = useGetPositionsForIncident();
  const notify = useCreateNotification();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);

  const userOptions = useMemo(
    () =>
      roster
        .filter((member) => member.active !== 0 && member.userId)
        .map((member) => ({
          value: member.userId,
          label: member.userName || member.emailAddress || member.userId,
        })),
    [roster]
  );

  const positionOptions = useMemo(
    () =>
      positions
        .filter((position) => !position.isResourcePosition)
        .map((position) => ({
          value: position.id,
          label: position.name,
        })),
    [positions]
  );

  const send = async () => {
    const fromPositions = roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          positionIds.includes(position.positionId)
        )
      )
      .map((member) => member.userId);
    const recipients = [...new Set([...userIds, ...fromPositions])].filter(
      Boolean
    );
    if (!incidentId || recipients.length === 0) return;
    // Stamp the request signature first so a failed save does not notify.
    await onConfirm();
    const path = `/iap/${incidentId}?openTab=Actions&openAction=${actionId}`;
    const payload = {
      incidentId,
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: null,
      emailMessage: null,
      smsMessage: null,
      teamsMessage: null,
      phoneMessage: null,
      appMessage: buildTaskOpenAppMessage({
        prefix: `${title}.`,
        taskName,
        path,
      }),
      userIds: recipients,
      teamIds: [],
      // notifications.source_item_id FK → item_reference; actions are not item_reference rows.
      sourceItemId: null,
      sourceItemType: null,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
    };
    await notify.mutateAsync(payload);
    setUserIds([]);
    setPositionIds([]);
    onOpenChange(false);
  };

  const pending = notify.isPending || confirmPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Users
            </div>
            <MultiIdCombobox
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Select users…"
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Positions
            </div>
            <MultiIdCombobox
              options={positionOptions}
              value={positionIds}
              onChange={setPositionIds}
              placeholder="Select positions…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || (!userIds.length && !positionIds.length)}
            onClick={() => void send()}
          >
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
